import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isMobile } from "react-device-detect";
import { setClass } from "@utils";
import { Input } from "../input";
import { ArrowBottom, Cross, Search } from "../svgs";
import { OpenCloseIcon } from "../svgs/OpenCloseIcon";
import "../../styles/components/collapsibleList.scss";

interface CollapsibleListProps<T> {
  listItems: T[];
  title?: string;
  icon?: ReactNode;
  button?: ReactNode;
  maxHeight?: number;
  placeholder?: string;
  renderOption: (option: T) => ReactNode;
  onClickIcon?: () => void;
  getSearchOption?: (option: T) => string;
  onEnterPress?: (option: T) => void;
}

export const CollapsibleList = <T,>({
  listItems,
  title,
  icon,
  placeholder,
  onClickIcon,
  renderOption,
  getSearchOption,
  onEnterPress,
  button,
  maxHeight,
}: CollapsibleListProps<T>) => {
  const [isClose, setIsClose] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filteredItems, setFilteredItems] = useState(listItems);

  useEffect(() => {
    setFilteredItems(
      getSearchOption
        ? listItems.filter((item) =>
            getSearchOption(item)
              ?.toLowerCase()
              .includes(searchValue.toLowerCase())
          )
        : listItems
    );
  }, [searchValue, listItems]);

  const onClose = () => {
    setIsClose((prev) => !prev);
    !isClose && setIsSearch(false);
    // setTimeout здесь нужен, чтобы сначала закрылся аккордион, а потом filteredItems сбросился, так не будет резких скачков
    setTimeout(() => !isClose && setFilteredItems(listItems), 300);
  };

  const onToggleSearch = useCallback(() => {
    setSearchValue("");
    setIsSearch((prev) => !prev);
    setIsClose(false);
  }, []);

  const getClassNameForList = useMemo(
    () => setClass("collapsible-list-items", isClose && "close"),
    [isClose]
  );

  const onSelectRemaining = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && filteredItems.length === 1) {
      onEnterPress(filteredItems[0]);
      onToggleSearch();
    }
  };

  const getStyleForList = useMemo(
    () => (maxHeight ? { maxHeight } : {}),
    [maxHeight]
  );

  return (
    <div className={setClass("collapsible-list", isClose && "close")}>
      <div className="header" onKeyDown={onSelectRemaining}>
        {getSearchOption && isSearch ? (
          <Input
            autoFocus
            variant={"underlined"}
            placeholder="Поиск"
            value={searchValue}
            onChange={setSearchValue}
            endElement={
              <Cross width={16} height={17} onClick={onToggleSearch} />
            }
          />
        ) : (
          <div className={setClass("info", isMobile && "mobile")}>
            <div className="title">{title}</div>
            {button}
            <div className="icons">
              {icon && (
                <div className="icon" onClick={onClickIcon}>
                  {icon}
                </div>
              )}
              {getSearchOption && (
                <div className="icon" onClick={onToggleSearch}>
                  <Search hexFill="#000000" />
                </div>
              )}
            </div>
          </div>
        )}
        <div className="arrow" onClick={onClose}>
          {isMobile ? (
            <OpenCloseIcon isClose={isClose} />
          ) : (
            <ArrowBottom isReverse={!isClose} />
          )}
        </div>
      </div>
      <div className={getClassNameForList} style={getStyleForList}>
        {!!filteredItems.length ? (
          filteredItems.map((item) => renderOption(item))
        ) : (
          <div className="placeholder">{placeholder}</div>
        )}
      </div>
    </div>
  );
};
